import { useEffect, useMemo, useState } from 'react';
import { costSettingsSchema, type AppSettings, type CostSettings } from '@ace-tracker/shared';
import { AmountInput, Card, Field, SaveBar, TextInput, amountToString, stringToAmount } from './formParts';
import { useSaveSettings } from './useSaveSettings';

interface Draft {
  currency: string;
  copilotCostPerAiu: string;
  source: string;
  input: string;
  output: string;
  cacheRead: string;
  cacheWrite: string;
  autoApplyPriceFeed: boolean;
}

function toDraft(cost: CostSettings): Draft {
  return {
    currency: cost.currency,
    copilotCostPerAiu: amountToString(cost.copilotCostPerAiu),
    source: cost.source,
    input: amountToString(cost.fallbackPricing.inputPerMillion),
    output: amountToString(cost.fallbackPricing.outputPerMillion),
    cacheRead: amountToString(cost.fallbackPricing.cacheReadPerMillion),
    cacheWrite: amountToString(cost.fallbackPricing.cacheWritePerMillion),
    autoApplyPriceFeed: cost.autoApplyPriceFeed,
  };
}

function toCost(d: Draft) {
  const required = (v: string) => (v.trim() === '' ? NaN : Number(v));
  return {
    currency: d.currency.trim().toUpperCase(),
    copilotCostPerAiu: required(d.copilotCostPerAiu),
    source: d.source.trim(),
    fallbackPricing: {
      inputPerMillion: required(d.input),
      outputPerMillion: required(d.output),
      cacheReadPerMillion: stringToAmount(d.cacheRead),
      cacheWritePerMillion: stringToAmount(d.cacheWrite),
    },
    autoApplyPriceFeed: d.autoApplyPriceFeed,
  };
}

const FIELD_LABELS: Record<string, string> = {
  currency: 'Currency',
  copilotCostPerAiu: 'Copilot planning rate per AIU (legacy)',
  source: 'Cost basis',
  inputPerMillion: 'Fallback input price',
  outputPerMillion: 'Fallback output price',
  cacheReadPerMillion: 'Fallback cache-read price',
  cacheWritePerMillion: 'Fallback cache-write price',
};

export function CostSettingsForm({ settings }: { settings: AppSettings }) {
  const baseline = settings.cost;
  const [draft, setDraft] = useState<Draft>(() => toDraft(baseline));
  const [validationError, setValidationError] = useState<string>();
  const { save, saving, error, savedAt, reset } = useSaveSettings('cost');
  const baselineJson = useMemo(() => JSON.stringify(toCost(toDraft(baseline))), [baseline]);

  useEffect(() => setDraft(toDraft(baseline)), [baseline]);

  const dirty = JSON.stringify(toCost(draft)) !== baselineJson;
  const currencyChanged = draft.currency.trim().toUpperCase() !== baseline.currency;

  function set(patch: Partial<Draft>) {
    setValidationError(undefined);
    setDraft((d) => ({ ...d, ...patch }));
  }

  function handleSave() {
    const parsed = costSettingsSchema.safeParse(toCost(draft));
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const field = FIELD_LABELS[String(issue.path[issue.path.length - 1])] ?? 'Value';
      setValidationError(`${field}: ${issue.message === 'Expected number, received nan' ? 'enter a number' : issue.message}`);
      return;
    }
    save(parsed.data);
  }

  const cur = draft.currency.trim().toUpperCase() || baseline.currency;

  return (
    <div className="space-y-5">
      <Card
        title="Currency & cost basis"
        description="Applies to every indicative cost in the dashboard. Costs are planning estimates, not invoices."
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field label="Currency" hint="Three-letter ISO 4217 code, e.g. USD, EUR, INR." htmlFor="cost-currency">
            <TextInput
              id="cost-currency"
              className="max-w-[8rem] uppercase"
              maxLength={3}
              value={draft.currency}
              invalid={!/^[A-Za-z]{3}$/.test(draft.currency.trim())}
              onChange={(e) => set({ currency: e.target.value })}
            />
          </Field>
          <Field
            label="Cost basis"
            hint="Shown next to every estimate, e.g. “Contracted rates, FY26”."
            htmlFor="cost-source"
          >
            <TextInput
              id="cost-source"
              maxLength={300}
              value={draft.source}
              invalid={!draft.source.trim()}
              onChange={(e) => set({ source: e.target.value })}
            />
          </Field>
        </div>
        {currencyChanged && (
          <p className="mt-4 rounded-input bg-warn-bg px-3 py-2 text-xs text-text-default">
            <i aria-hidden="true" className="fa-solid fa-triangle-exclamation mr-1.5 text-warn-ink" />
            Changing the currency doesn't convert any prices — update the model pricing table and the rates below to{' '}
            {cur} too.
          </p>
        )}
      </Card>

      <Card
        title="GitHub Copilot"
        description="Planning rate for the legacy AIU estimate on the Copilot tab (recorded AIU × this rate). GitHub bills Copilot in AI credits, so this estimate is not current spend and is left out of the Executive and All Tools spend figures."
      >
        <div className="max-w-xs">
          <Field label={`Cost per AIU (${cur})`} htmlFor="cost-aiu">
            <AmountInput
              id="cost-aiu"
              label={`Cost per AIU (${cur})`}
              value={draft.copilotCostPerAiu}
              onChange={(v) => set({ copilotCostPerAiu: v })}
            />
          </Field>
        </div>
      </Card>

      <Card
        title="Fallback token pricing"
        description={`Used for any Claude Code or Codex model with no row in Model pricing, in ${cur} per million tokens. The Model pricing tab lists which models currently fall back.`}
      >
        <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
          <Field label="Input" htmlFor="fb-input">
            <AmountInput id="fb-input" label="Fallback input price" value={draft.input} onChange={(v) => set({ input: v })} />
          </Field>
          <Field label="Output" htmlFor="fb-output">
            <AmountInput id="fb-output" label="Fallback output price" value={draft.output} onChange={(v) => set({ output: v })} />
          </Field>
          <Field label="Cache read" htmlFor="fb-cache-read">
            <AmountInput
              id="fb-cache-read"
              label="Fallback cache-read price"
              allowEmpty
              placeholder="—"
              value={draft.cacheRead}
              onChange={(v) => set({ cacheRead: v })}
            />
          </Field>
          <Field label="Cache write" htmlFor="fb-cache-write">
            <AmountInput
              id="fb-cache-write"
              label="Fallback cache-write price"
              allowEmpty
              placeholder="—"
              value={draft.cacheWrite}
              onChange={(v) => set({ cacheWrite: v })}
            />
          </Field>
        </div>
      </Card>

      <Card
        title="List-price feed"
        description="The API checks public list prices daily (pydantic/genai-prices, USD). By default an admin reviews and applies them on the Model pricing tab."
      >
        <label className="flex min-h-[44px] cursor-pointer items-start gap-3 text-sm text-text-default">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-primary-blue"
            checked={draft.autoApplyPriceFeed}
            onChange={(e) => set({ autoApplyPriceFeed: e.target.checked })}
          />
          <span>
            Apply list prices automatically
            <span className="mt-0.5 block text-xs text-text-muted">
              Overwrites model pricing rows that differ from the feed after each check, keeping dated price history. Leave off
              if the rows hold contracted rates. Only works while the currency is USD.
            </span>
          </span>
        </label>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={saving}
        error={validationError ?? error}
        savedAt={savedAt}
        audit={settings.audit.cost}
        onSave={handleSave}
        onDiscard={() => {
          setDraft(toDraft(baseline));
          setValidationError(undefined);
          reset();
        }}
      />
    </div>
  );
}
